import { clearUserFactorLockouts, dirSyncProgress, findDirectories } from '@api/portalServices';
import type { Directory, ImprUser, SyncStatus, User } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button as ImprButton, type ButtonProps } from '@imprivata-cloud/components';
import { useMutation } from '@tanstack/react-query';
import { Dropdown, Flex, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { featureSupported } from '../../../featureFlagsUtils';
import MenuIcon from '../../../assets/icons/kabob_menu.svg?react';
import GroupsModal from './groups/GroupsModal';
import useInfiniteScroll from './hooks/useInfiniteScroll';
import ImprUserList from './impr-list-view/ImprUserList';
import Sync from './sync/Sync';
import TestEntraAuthModal from './test-auth/TestEntraAuthModal';
import UsersEmptyPage from './UsersEmptyPage';

const ENABLE_TEST_ENTRA_ID = featureSupported('ENABLE_TEST_ENTRA_ID');

const Users = () => {
  const { t } = useTranslation();
  const { tenantId } = useSession();
  const [directories, setDirectories] = useState<Directory[]>();
  const { emitError } = useErrorContext();
  const [initSyncStatus, setInitSyncStatus] = useState<SyncStatus>();
  const lastElementRef = useRef<HTMLDivElement | null>(null);
  const [searchVal, setSearchVal] = useState<string>('');
  const [unlockedUserIds, setUnlockedUserIds] = useState<Set<string>>(new Set());
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [testAuthOpen, setTestAuthOpen] = useState(false);
  const { mutate: clearLockoutsMutation, isPending: isUnlockPending } = useMutation({
    mutationFn: (data: { userId: string; factorTypes: string[] }) => clearUserFactorLockouts(data),
  });

  const {
    data: userList = [],
    isFetchingNextPage,
    refetch,
    isLoading,
  } = useInfiniteScroll({ lastElementRef, searchTerm: searchVal });

  const directoriesLoaded = useRef(false);

  const handleSearch = () => {
    refetch();
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchVal(searchValue);
  };

  const showUnlockButton = useCallback((user: ImprUser | User): boolean => {
    const userId = user.userId;
    if (!userId) {
      return false;
    }
    return (
      (user as User).factorLockouts?.some(f => new Date(f.lockedUntil).getTime() > Date.now()) ??
      false
    );
  }, []);

  const unlockUser = useCallback(
    (userId: string, _hasAccountLock: boolean, factorTypes: string[]) => {
      clearLockoutsMutation(
        { userId, factorTypes },
        {
          onSuccess: () => {
            setUnlockedUserIds(prev => new Set(prev).add(userId));
          },
          onError: (e: unknown) => {
            emitError(new AppError(e as ApiError));
          },
        },
      );
    },
    [clearLockoutsMutation, emitError],
  );

  const loadUsers = useCallback(async () => {
    console.log('[Users] loadUsers');
    refetch();
  }, [refetch]);

  useEffect(() => {
    const fetchDirectories = async () => {
      try {
        const response = await findDirectories(tenantId ?? '');
        const directories = response?.directories;
        if (directories && directories[0]?.syncedGroupCount === 0) {
          try {
            const resp = await dirSyncProgress(tenantId || '', directories[0]?.directoryId);
            setInitSyncStatus(resp?.syncStatus);
          } catch (err) {
            emitError(err as AppError);
          }
        }
        setDirectories(directories);
      } catch (err) {
        emitError(err as AppError);
      }
    };
    if (!directoriesLoaded.current) {
      directoriesLoaded.current = true;
      void fetchDirectories();
    }
  }, [tenantId, emitError]);

  const menuButtonProps: ButtonProps = {
    icon: <MenuIcon />,
    size: 'small',
    style: { borderColor: 'transparent', boxShadow: 'none', marginLeft: 'auto' },
  };

  const hasDirectory = Boolean(directories?.[0]?.directoryId);
  // userList is filtered by searchVal, so an empty list under an active search
  // does not imply the tenant has no synced users. Only treat the tenant as
  // having no synced users when the unfiltered list is empty.
  const hasSyncedUsers = userList.length > 0 || searchVal.trim().length > 0;

  if (isLoading) {
    return (
      <Flex justify={'center'} style={{ padding: 15 }}>
        <Spin data-testid={'loading-indicator'} size="small" />
      </Flex>
    );
  }

  return (
    <div>
      <ImprUserList
        dataSource={userList}
        customEmptyListNode={
          <UsersEmptyPage directories={directories} initSyncStatus={initSyncStatus} />
        }
        handleSearch={handleSearch}
        onSearchChange={handleSearchChange}
        rowKey={'upn'}
        loading={isLoading}
        enableUnlock={showUnlockButton}
        unlockUser={unlockUser}
        isPending={isUnlockPending}
        isUnlocked={(userId: string) => unlockedUserIds.has(userId)}
        headerExtraComponents={[
          hasDirectory ? (
            <>
              <Sync
                key={'manualSync'}
                tenantId={tenantId}
                directoryId={directories?.[0]?.directoryId}
                loadUsers={loadUsers}
              />
              <Dropdown
                key={'directoryMenu'}
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'manage-groups',
                      label: t('portal.settings.groups.title'),
                      onClick: () => setGroupsOpen(true),
                    },
                    ...(ENABLE_TEST_ENTRA_ID
                      ? [
                          {
                            key: 'test-entra-id',
                            label: t('portal.test-entra-id.title'),
                            onClick: () => setTestAuthOpen(true),
                          },
                        ]
                      : []),
                  ],
                }}
              >
                <ImprButton
                  {...menuButtonProps}
                  aria-label={t('portal.settings.identity-users.directory-menu')}
                />
              </Dropdown>
            </>
          ) : (
            <div key={'emptyUserMenu'} />
          ),
        ]}
      />
      {groupsOpen && (
        <GroupsModal
          open={groupsOpen}
          onClose={() => setGroupsOpen(false)}
          initSyncStatus={initSyncStatus}
          tenantId={tenantId || ''}
        />
      )}
      <TestEntraAuthModal
        open={testAuthOpen}
        onClose={() => setTestAuthOpen(false)}
        tenantId={tenantId || ''}
        hasSyncedUsers={hasSyncedUsers}
      />
      <div ref={lastElementRef} data-testid="user-list-last-element">
        {isFetchingNextPage && (
          <Flex justify={'center'} style={{ padding: 15 }}>
            <Spin data-testid={'loading-indicator'} size="small" />
          </Flex>
        )}
      </div>
    </div>
  );
};

export default Users;
